import { useEffect, useRef } from 'react';
import { EmptyState } from '../../ui/EmptyState/EmptyState';
import Loader from '../../ui/Loader/Loader';
import { AISystemsTableProps } from './AISystemsTable.type';
import { TableHeader } from './components/TableHeader/TableHeader';
import { TableRow } from './components/TableRow/TableRow';
import { Search } from '../../../icons/SvgIcons';

export function AISystemsTable({
  aiSystems,
  onSelect,
  loading = false,
  hasMore = false,
  onLoadMore,
  onDelete,
  sortBy,
  sortOrder,
  onSort
}: AISystemsTableProps) {
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          onLoadMore?.();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, loading, onLoadMore]);

  if (loading && aiSystems.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  if (aiSystems.length === 0 && !loading) {
    return (
      <EmptyState
        icon={Search()}
        // icon={<img src={SearchIcon} alt="No data" className="w-9 h-9" />}
        title="No AI Systems matched your criteria."
        description="Try adjusting your search or filters."
      />
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="w-full overflow-hidden rounded-lg bg-white shadow-sm border border-solid border-gray-250v2">
        <table className="w-full table-fixed">
          <TableHeader
            type="AISystemOverview"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={onSort}
          />
          <tbody>
            {aiSystems.length > 0 &&
              aiSystems.map((system) => (
                <TableRow
                  key={system.id}
                  system={system}
                  onSelect={onSelect}
                  onDelete={onDelete}
                />
              ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div ref={observerTarget} className="flex justify-center py-4">
          {loading && <Loader />}
        </div>
      )}
    </div>
  );
}
